import React, { useState, useEffect, useCallback, useRef } from "react";
import "./SubscriptionsPage.css";
import CardDetailsPopup from "../components/CardDetailsForm";
import PlanChangePopup from "../components/ChangePlanForm";
import { useCustom } from "../hooks/useCustomization.js";
import {
  AsYouType,
  CountryCode,
  parsePhoneNumberFromString,
  getCountryCallingCode,
} from "libphonenumber-js";
import { getExampleNumber } from "libphonenumber-js/core";
import metadata from "libphonenumber-js/metadata.full.json";
import examples from "libphonenumber-js/examples.mobile.json";
import { COUNTRY_OPTIONS } from "../utils/dialCodes";
import { useAuthentication } from "../context/AuthContext";
import { useModules } from "../context/ModulesContext";
import ColorWheelPicker, { GlowValue } from "../components/ColorWheelPicker";

// ─── Types ────────────────────────────────────────────────────────────────────

type SubscriptionFields = {
  planName: string;
  planPeriod: string;
  nextChargeDate: string;
  paymentMethod: string;
  companyName: string;
  legalName: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  companyIdentifier: string;
  address1: string;
  address2: string;
  country: string;
  stateProvince: string;
  postalCode: string;
  bannerTitle: string;
  bannerColour: string;
  textColour: string;
};

const defaultSubscriptionFields: SubscriptionFields = {
  planName: "",
  planPeriod: "",
  nextChargeDate: "",
  paymentMethod: "",
  companyName: "",
  legalName: "",
  email: "",
  phoneNumber: "",
  countryCode: "",
  companyIdentifier: "",
  address1: "",
  address2: "",
  country: "",
  stateProvince: "",
  postalCode: "",
  bannerTitle: "",
  bannerColour: "",
  textColour: "",
};

// Glow is stored separately from the flat string fields since it's a
// { colour, intensity } pair. Default is "no glow" (black, 0% intensity).
const DEFAULT_GLOW: GlowValue = { color: "#000000", intensity: 0 };

type Status = "active" | "trialing" | "past_due" | "suspended" | "canceled";

const COUNTRY_MAP = new Map(
  COUNTRY_OPTIONS.map(
    (c: { name: string; dial_code: string; code: string }) => [c.code, c],
  ),
);

// ─── Component ────────────────────────────────────────────────────────────────

export default function SubscriptionsPage() {
  const { setCustomization, customization } = useCustom();
  // eslint-disable-next-line
  const { org, entitlements, roles, refreshEntitlements } = useAuthentication();
  const { modules: MODULE_REGISTRY } = useModules();

  // ── Subscription state (unchanged) ──────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCardPopup, setShowCardPopup] = useState(false);
  const [showPlanPopup, setShowPlanPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showUnsubPopup, setShowUnsubPopup] = useState(false);
  const [copied, setCopied] = useState(false);

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [isMfaUpdating, setIsMfaUpdating] = useState(false);

  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const digitsRef = useRef("");
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const [initialData, setInitialData] = useState<SubscriptionFields>(
    defaultSubscriptionFields,
  );
  const [fields, setFields] = useState<SubscriptionFields>(
    defaultSubscriptionFields,
  );
  // Glow (banner glow colour + intensity) tracked separately from `fields`.
  const [glow, setGlow] = useState<GlowValue>(DEFAULT_GLOW);
  const [initialGlow, setInitialGlow] = useState<GlowValue>(DEFAULT_GLOW);
  const [status, setStatus] = useState<Status>("active");
  const [cancelled] = useState<boolean>(false);

  // ── Subscription handlers (unchanged) ────────────────────────────────────────

  const handleInputChange = useCallback(
    (field: keyof SubscriptionFields, value: string) => {
      setFields((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const isModified = useCallback(
    (field: keyof SubscriptionFields): boolean =>
      fields[field] !== initialData[field],
    [fields, initialData],
  );

  const handleConfirmCancel = useCallback(() => {
    setShowUnsubPopup(false);
  }, []);

  const onUpdateFields = useCallback(async () => {
    setIsUpdating(true);
    const excludedFields = [
      "planName",
      "planPeriod",
      "nextChargeDate",
      "paymentMethod",
    ];
    const changedFields: Partial<SubscriptionFieldsWithoutPlan> = {};
    for (const key of Object.keys(fields) as (keyof SubscriptionFields)[]) {
      if (!excludedFields.includes(key) && fields[key] !== initialData[key]) {
        changedFields[key as keyof SubscriptionFieldsWithoutPlan] = fields[key];
      }
    }
    const glowChanged =
      glow.color !== initialGlow.color || glow.intensity !== initialGlow.intensity;

    if (Object.keys(changedFields).length === 0 && !glowChanged) {
      setIsUpdating(false);
      return;
    }
    try {
      await updateSubscriptionFields({
        ...changedFields,
        ...(glowChanged ? { glow } : {}),
      });
      setInitialData(fields);
      setInitialGlow(glow);
      setCustomization({
        ...customization,
        branding: {
          ...customization.branding,
          title: fields.bannerTitle,
          headerBgColor: fields.bannerColour,
          headerTxtColor: fields.textColour,
          glow,
        },
      });
    } catch (err) {
      console.error(err);
      setShowErrorPopup(true);
    } finally {
      setIsUpdating(false);
    }
  }, [setCustomization, customization, fields, initialData, glow, initialGlow]);

  async function handlePlanChange(
    plan: string,
    period: "monthly" | "annually",
  ) {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/subscription/plan`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ plan, period }),
        },
      );
      if (!resp.ok)
        throw new Error(`Failed to update plan: ${await resp.text()}`);
      const data = await resp.json();
      setFields((prev) => ({
        ...prev,
        planName: data.planName,
        nextChargeDate: data.nextInvoiceDate,
        planPeriod: data.period,
      }));
      alert(
        `Plan changed to ${plan}. You'll be charged for it starting ${data.nextInvoiceDate}`,
      );
    } catch (err) {
      console.error(err);
      alert("Failed to change the plan. Please try again.");
    } finally {
      setShowPlanPopup(false);
    }
  }

  async function updateIdentifierString() {
    const confirmed = window.confirm(
      "Are you sure you want to rotate the company identifier? This will invalidate the old one.",
    );
    if (!confirmed) return;
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/subscription/rotate-company-id`,
        { method: "POST", credentials: "include" },
      );
      if (!resp.ok)
        throw new Error(
          `Failed to rotate company identifier: ${await resp.text()}`,
        );
      const data = await resp.json();
      if (!data.companyIdentifier)
        throw new Error("Response missing companyIdentifier");
      setFields((prev) => ({
        ...prev,
        companyIdentifier: data.companyIdentifier,
      }));
      setInitialData(
        (prev): SubscriptionFields => ({
          ...prev,
          companyIdentifier: data.companyIdentifier,
        }),
      );
    } catch (err) {
      console.error("Rotation failed:", err);
      window.alert("Failed to rotate identifier. Please try again.");
    }
  }

  async function handleMfaToggle(enabled: boolean) {
    setIsMfaUpdating(true);
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/subscription/mfa`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enabled }),
        },
      );
      if (!resp.ok)
        throw new Error(`Failed to update MFA settings: ${await resp.text()}`);
      setMfaEnabled(enabled);
    } catch (err) {
      console.error(err);
      window.alert("Failed to update MFA settings. Please try again.");
    } finally {
      setIsMfaUpdating(false);
    }
  }

  async function submitPaymentMethod(paymentMethodID: string) {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/subscription/payment-method`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ paymentMethodId: paymentMethodID }),
        },
      );
      if (!resp.ok)
        throw new Error(
          `Failed to update payment method: ${await resp.text()}`,
        );
      const data = await resp.json();
      if (!data.paymentMethodDisplay)
        throw new Error("Response missing 'paymentMethodDisplay' field");
      setFields((prev) => ({
        ...prev,
        paymentMethod: data.paymentMethodDisplay,
      }));
      setInitialData((prev) => ({
        ...prev,
        paymentMethod: data.paymentMethodDisplay,
      }));
      return data.paymentMethodDisplay;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setShowCardPopup(false);
    }
  }

  const statusLabels: Record<Status, string> = {
    active: "Next Invoice:",
    trialing: "Trial ends on",
    past_due: "Last due date:",
    suspended: "Suspended on:",
    canceled: "Canceled on:",
  };
  const setDateMessage = () => statusLabels[status];

  const formatNumber = (digits: string, countryCode: CountryCode) =>
    new AsYouType(countryCode).input(digits);

  const resetPhoneFields = (code: string) => {
    setSelectedCountryCode(code);
    setPhoneDisplay("");
    digitsRef.current = "";
    handleInputChange("phoneNumber", "");
    handleInputChange("countryCode", code);
  };

  const handlePhoneChange = (input: string) => {
    if (!selectedCountryCode) return;
    const digitsOnly = input.replace(/\D/g, "");
    const example = getExampleNumber(
      selectedCountryCode as CountryCode,
      examples,
      metadata,
    );
    const maxLength = example?.nationalNumber?.length || 10;
    const limitedDigits = digitsOnly.slice(0, maxLength);
    digitsRef.current = limitedDigits;
    const formatted = formatNumber(
      limitedDigits,
      selectedCountryCode as CountryCode,
    );
    setPhoneDisplay(formatted);
    handleInputChange(
      "phoneNumber",
      `+${getCountryCallingCode(selectedCountryCode as CountryCode)}${limitedDigits}`,
    );
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!selectedCountryCode || e.key !== "Backspace") return;
    e.preventDefault();
    const currentDigits = digitsRef.current;
    if (currentDigits.length === 0) return;
    const newDigits = currentDigits.slice(0, -1);
    digitsRef.current = newDigits;
    const formatted = formatNumber(
      newDigits,
      selectedCountryCode as CountryCode,
    );
    setPhoneDisplay(formatted);
    handleInputChange(
      "phoneNumber",
      `+${getCountryCallingCode(selectedCountryCode as CountryCode)}${newDigits}`,
    );
    setTimeout(() => {
      if (phoneInputRef.current)
        phoneInputRef.current.setSelectionRange(
          formatted.length,
          formatted.length,
        );
    }, 0);
  };

  // ── Effects ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    const onLoad = async () => {
      try {
        setIsLoading(true);
        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/subscription`,
          { method: "GET", credentials: "include" },
        );
        if (!resp.ok)
          throw new Error(`Failed to fetch subscription: ${await resp.text()}`);
        const data = await resp.json();
        if (
          !data?.planDetails?.plan ||
          !data?.orgDetails?.legalName ||
          !data?.customizationDetails
        ) {
          throw new Error("Incomplete subscription data");
        }
        const merged: SubscriptionFields = {
          planName: data.planDetails.plan || "",
          nextChargeDate: data.planDetails.nextChargeDate || "",
          paymentMethod: data.planDetails.paymentMethod || "",
          companyName: data.orgDetails.companyName || "",
          legalName: data.orgDetails.legalName || "",
          email: data.orgDetails.email || "",
          phoneNumber: data.orgDetails.phoneNumber || "",
          companyIdentifier: data.orgDetails.companyIdentifier || "",
          address1: data.orgDetails.address1 || "",
          address2: data.orgDetails.address2 || "",
          country: data.orgDetails.country || "",
          stateProvince: data.orgDetails.state || "",
          postalCode: data.orgDetails.postalCode || "",
          bannerTitle: data.customizationDetails.bannerTitle || "",
          bannerColour: data.customizationDetails.bannerColour || "",
          textColour: data.customizationDetails.textColour || "",
          planPeriod: data.planDetails.period || "",
          countryCode: data.orgDetails.countryCode || "",
        };
        setInitialData(merged);
        setFields(merged);
        const loadedGlow: GlowValue = data.customizationDetails.glow
          ? {
              color: data.customizationDetails.glow.color || DEFAULT_GLOW.color,
              intensity:
                typeof data.customizationDetails.glow.intensity === "number"
                  ? data.customizationDetails.glow.intensity
                  : DEFAULT_GLOW.intensity,
            }
          : DEFAULT_GLOW;
        setGlow(loadedGlow);
        setInitialGlow(loadedGlow);
        setStatus(data.status);
        setMfaEnabled(data.orgDetails.mfaEnabled ?? false);
        setSelectedCountryCode(merged.countryCode);
        const parsed = parsePhoneNumberFromString(
          merged.phoneNumber,
          merged.countryCode as CountryCode,
        );
        if (parsed) {
          const national = parsed.nationalNumber;
          digitsRef.current = national;
          setPhoneDisplay(
            formatNumber(national, merged.countryCode as CountryCode),
          );
        } else {
          console.warn("Failed to parse phone number:", merged.phoneNumber);
          setPhoneDisplay("");
        }
      } catch (err) {
        console.error("Error fetching subscription data:", err);
        setShowErrorPopup(true);
      } finally {
        setIsLoading(false);
      }
    };
    onLoad();
  }, []);

  useEffect(() => {
    if (showErrorPopup) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showErrorPopup]);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="subscriptions-page">
      <div className="subscriptions-header">
        <h2 className="subscriptions-title">Manage Subscription</h2>
      </div>

      {/* ── Plan Details (unchanged) ── */}
      <div className="subscription-container">
        <div className="subscription-container-header">Plan Details</div>
        <div className="subscription-container-body">
          <div className="plan-current-info">
            <div className="plan-line">
              Current Plan:{" "}
              <strong>
                {fields.planName || "---"} {status === "trialing" && "(Trial)"}
              </strong>
            </div>
            <div className="plan-line">
              {setDateMessage()}{" "}
              <strong>
                {fields.nextChargeDate || "---"} {cancelled && "(Ending)"}
              </strong>
            </div>
            {cancelled || status === "canceled" ? (
              <button
                className="change-plan-link"
                onClick={() => {
                  alert("yes");
                }}
              >
                Resubscribe
              </button>
            ) : (
              <button
                type="button"
                className="change-plan-link"
                onClick={() => setShowPlanPopup(true)}
              >
                Change Plan
              </button>
            )}
          </div>
          <div className="payment-divider">
            <div className="divider-label">Payment Method</div>
            <hr />
          </div>
          <div className="payment-method-row">
            <div className="payment-method-left">
              <div className="payment-method-label">Saved payment method:</div>
              <div className="card-icon">
                {fields.paymentMethod || "No Card on record."}
              </div>
            </div>
            <div className="payment-method-right">
              <button
                className="manage-subscription-action-button"
                onClick={() => setShowCardPopup(true)}
              >
                Change Payment Method
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Organization Details (unchanged) ── */}
      <div className="subscription-container">
        <div className="subscription-container-header">
          Organization Details
        </div>
        <div className="subscription-container-body organization-details-section">
          <div className="field-group full-width">
            <label className="field-label" htmlFor="companyName">
              Company Name
            </label>
            <input
              disabled
              id="companyName"
              autoComplete="off"
              className={`custom-input ${isModified("companyName") ? "modified" : ""}`}
              value={fields.companyName}
              onChange={(e) => handleInputChange("companyName", e.target.value)}
            />
          </div>
          <div className="field-group full-width">
            <label className="field-label" htmlFor="legalName">
              Legal Name
            </label>
            <input
              disabled
              id="legalName"
              autoComplete="off"
              className={`custom-input ${isModified("legalName") ? "modified" : ""}`}
              value={fields.legalName}
              onChange={(e) => handleInputChange("legalName", e.target.value)}
            />
          </div>
          <div className="field-group full-width">
            <label className="field-label" htmlFor="address1">
              Address 1
            </label>
            <input
              id="address1"
              autoComplete="off"
              className={`custom-input ${isModified("address1") ? "modified" : ""}`}
              value={fields.address1}
              onChange={(e) => handleInputChange("address1", e.target.value)}
            />
          </div>
          <div className="field-group full-width">
            <label className="field-label" htmlFor="address2">
              Address 2
            </label>
            <input
              id="address2"
              autoComplete="off"
              className={`custom-input ${isModified("address2") ? "modified" : ""}`}
              value={fields.address2}
              onChange={(e) => handleInputChange("address2", e.target.value)}
            />
          </div>
          <div className="horizontal-fields">
            <div className="field-group">
              <label className="field-label" htmlFor="country">
                Country
              </label>
              <input
                id="country"
                autoComplete="off"
                className={`custom-input ${isModified("country") ? "modified" : ""}`}
                value={fields.country}
                onChange={(e) => handleInputChange("country", e.target.value)}
              />
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor="state">
                State/Territory
              </label>
              <input
                id="state"
                autoComplete="off"
                className={`custom-input ${isModified("stateProvince") ? "modified" : ""}`}
                value={fields.stateProvince}
                onChange={(e) =>
                  handleInputChange("stateProvince", e.target.value)
                }
              />
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor="zip">
                Zip/Postal Code
              </label>
              <input
                id="zip"
                autoComplete="off"
                className={`custom-input ${isModified("postalCode") ? "modified" : ""}`}
                value={fields.postalCode}
                onChange={(e) =>
                  handleInputChange("postalCode", e.target.value)
                }
              />
            </div>
          </div>
          <div className="payment-divider">
            <div className="divider-label">Contact Info</div>
            <hr />
          </div>
          <div className="horizontal-fields">
            <div className="field-group">
              <label className="field-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className={`custom-input ${isModified("email") ? "modified" : ""}`}
                value={fields.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor="phone">
                Phone Number
              </label>
              <div className="subscription-page-phone-wrapper">
                <select
                  className="subscriotion-page-phone-country-selector"
                  value={selectedCountryCode}
                  onChange={(e) => {
                    const selected = COUNTRY_MAP.get(e.target.value);
                    if (selected) resetPhoneFields(selected.code);
                  }}
                >
                  <option hidden key="selected" value="">
                    {selectedCountryCode}{" "}
                    {COUNTRY_MAP.get(selectedCountryCode)?.dial_code}
                  </option>
                  {COUNTRY_OPTIONS.map(
                    (c: { name: string; dial_code: string; code: string }) => (
                      <option key={c.code} value={c.code}>
                        {c.name} {c.dial_code}
                      </option>
                    ),
                  )}
                </select>
                <input
                  type="tel"
                  id="phone"
                  inputMode="tel"
                  autoComplete="off"
                  disabled={!selectedCountryCode}
                  className={`custom-input ${isModified("phoneNumber") ? "modified" : ""}`}
                  value={phoneDisplay}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onKeyDown={handlePhoneKeyDown}
                  onPaste={(e) => e.preventDefault()}
                />
              </div>
            </div>
          </div>
          <div className="update-button-container">
            <button
              className="manage-subscription-action-button"
              disabled={isUpdating}
              onClick={onUpdateFields}
            >
              {isUpdating ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Organization Identifier (unchanged) ── */}
      <div className="subscription-container">
        <div className="subscription-container-header">
          Organization Identifier
          <p className="subtitle">Provide to your users for them to join</p>
        </div>
        <div className="subscription-container-body organization-details-section">
          <div className="field-group identifier-group">
            <div className="copy-wrapper">
              <input
                readOnly
                id="companyId"
                className={`custom-input copyable-input ${isModified("companyIdentifier") ? "modified" : ""}`}
                value={fields.companyIdentifier || "---"}
                onClick={() => {
                  navigator.clipboard.writeText(
                    fields.companyIdentifier || "copied",
                  );
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
              />
              {copied && <div className="copy-popup">Copied!</div>}
            </div>
            <button
              className="switch-org-button"
              title="Refresh Org. Identifier"
              onClick={updateIdentifierString}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="switch-icon"
              >
                <path
                  d="M4 12A8 8 0 0116 5.3M20 4v5h-5"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 12a8 8 0 01-12 6.7M4 20v-5h5"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── MFA Settings ── */}
      <div className="subscription-container">
        <div className="subscription-container-header">
          <div className="mfa-header-row">
            <span>Multi-Factor Authentication</span>
            <label className="mfa-switch">
              <input
                type="checkbox"
                checked={mfaEnabled}
                disabled={isMfaUpdating}
                onChange={(e) => handleMfaToggle(e.target.checked)}
              />
              <span className="mfa-slider" />
            </label>
          </div>
        </div>
      </div>

      {/* ── Customization (unchanged) ── */}
      <div className="subscription-container">
        <div className="subscription-container-header">Customization</div>
        <div className="subscription-container-body customization-section">
          <div className="field-group">
            <label className="field-label" htmlFor="bannerTitle">
              Banner Title
            </label>
            <input
              id="bannerTitle"
              name="bannerTitle"
              type="text"
              className={`custom-input ${isModified("bannerTitle") ? "modified" : ""}`}
              value={fields.bannerTitle || ""}
              onChange={(e) => handleInputChange("bannerTitle", e.target.value)}
            />
          </div>
          <div className="field-group color-picker-row">
            <div className="color-picker-group">
              <div className="field-label">Banner Colour</div>
              <ColorWheelPicker
                value={fields.bannerColour || "#218838"}
                ariaLabel="Banner colour"
                modified={isModified("bannerColour")}
                presets={["#218838", "#4B0000", "#2F5597"]}
                onChange={(hex) => handleInputChange("bannerColour", hex)}
              />
            </div>
            <div className="color-picker-group">
              <div className="field-label">Banner Text Colour</div>
              <ColorWheelPicker
                value={fields.textColour || "#ffffff"}
                ariaLabel="Banner text colour"
                modified={isModified("textColour")}
                onChange={(hex) => handleInputChange("textColour", hex)}
              />
            </div>
            <div className="color-picker-group">
              <div className="field-label">Glow</div>
              <ColorWheelPicker
                glow
                value={glow}
                ariaLabel="Glow"
                modified={
                  glow.color !== initialGlow.color ||
                  glow.intensity !== initialGlow.intensity
                }
                onChange={setGlow}
              />
            </div>
          </div>
          <div className="update-button-container">
            <button
              className="manage-subscription-action-button"
              disabled={isUpdating}
              onClick={onUpdateFields}
            >
              {isUpdating ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Default Landing Module ── */}
      <div className="subscription-container">
        <div className="subscription-container-header">Default Landing Module</div>
        <div style={{ padding: '1rem' }}>
          <p style={{ color: '#9aa0a6', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            Choose which module users see first after login.
          </p>
          <select
            className="manage-input"
            style={{ width: '100%', maxWidth: '300px' }}
            value={entitlements?.defaultModule || 'chatbot'}
            onChange={async (e) => {
              try {
                await fetch(`${process.env.REACT_APP_BACKEND_URL}/entitlements/default-module`, {
                  method: 'PUT',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ defaultModule: e.target.value }),
                });
                await refreshEntitlements();
              } catch (err) {
                console.error('Failed to update default module:', err);
              }
            }}
          >
            {MODULE_REGISTRY
              .filter(m => m.route && m.viewPermission)
              .filter(m => m.entitlementKey === null || entitlements?.features?.[m.entitlementKey]?.enabled === true)
              .filter(m => roles.permissions.includes(m.viewPermission!))
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map(m => (
                <option key={m.key} value={m.key}>{m.name}</option>
              ))}
          </select>
        </div>
      </div>

      {/* ── Unsubscribe ── */}
      {!cancelled && (
        <div className="righthand-side-btn-container">
          <button
            type="button"
            className="change-plan-link"
            onClick={() => setShowUnsubPopup(true)}
          >
            Unsubscribe
          </button>
        </div>
      )}

      {showCardPopup && (
        <CardDetailsPopup
          onCancel={() => setShowCardPopup(false)}
          onSubmitPaymentMethod={submitPaymentMethod}
        />
      )}

      {showPlanPopup && (
        <PlanChangePopup
          currentPlan={fields.planName as "Lite" | "Premium" | "Plus"}
          currentPeriod={fields.planPeriod as "monthly" | "annually"}
          onCancel={() => setShowPlanPopup(false)}
          onSubmit={(
            plan: "Lite" | "Premium" | "Plus",
            period: "monthly" | "annually",
          ) => {
            handlePlanChange(plan, period);
          }}
        />
      )}

      {showErrorPopup && (
        <div className="blocking-overlay">
          <div className="blocking-message-box">
            <h2>Data Unavailable</h2>
            <p>
              Subscription information could not be loaded. Please try again
              later or contact support.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="manage-subscription-action-button"
            >
              Reload Page
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="cdp-overlay">
          <div className="cdp-loading-spinner">Loading...</div>
        </div>
      )}

      {showUnsubPopup && !cancelled && (
        <div className="popup-overlay">
          <div className="popup-window">
            <p>
              Please confirm you wish to cancel your subscription at the next
              billing cycle.
            </p>
            <div className="popup-buttons">
              <button onClick={() => setShowUnsubPopup(false)}>Go Back</button>
              <button
                className="cancel-subscription-popup-btn"
                onClick={handleConfirmCancel}
              >
                Cancel Subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type SubscriptionFieldsWithoutPlan = Omit<
  SubscriptionFields,
  "planName" | "nextChargeDate" | "paymentMethod" | "planPeriod"
>;

async function updateSubscriptionFields(
  changedFields: Partial<SubscriptionFieldsWithoutPlan> & { glow?: GlowValue },
) {
  const resp = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/subscription`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(changedFields),
    },
  );
  if (!resp.ok)
    throw new Error(`Failed to update subscription: ${await resp.text()}`);
}

